import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { 
  Users, 
  Ticket, 
  Store, 
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Plus,
  Clock,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentVouchers, setRecentVouchers] = useState([]);
  const [recentBeneficiaries, setRecentBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, vouchersRes, beneficiariesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/vouchers?limit=5`),
        axios.get(`${API}/beneficiaries?limit=5`)
      ]);
      setStats(statsRes.data);
      setRecentVouchers(vouchersRes.data.items || []);
      setRecentBeneficiaries(beneficiariesRes.data.items || []);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-800 border-green-200",
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      redeemed: "bg-blue-100 text-blue-800 border-blue-200",
      expired: "bg-gray-100 text-gray-800 border-gray-200",
      cancelled: "bg-red-100 text-red-800 border-red-200"
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-20 bg-slate-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Beneficiaries",
      value: stats?.beneficiaries?.total || 0,
      subValue: `${stats?.beneficiaries?.approved || 0} approved`,
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      trend: stats?.beneficiaries?.pending > 0 ? `${stats.beneficiaries.pending} pending` : null,
      trendUp: false
    },
    {
      title: "Active Vouchers",
      value: stats?.vouchers?.active || 0,
      subValue: `${stats?.vouchers?.total || 0} total issued`,
      icon: Ticket,
      color: "text-amber-600",
      bgColor: "bg-amber-100",
      trend: `${stats?.vouchers?.redeemed || 0} redeemed`,
      trendUp: true
    },
    {
      title: "Partner Retailers",
      value: stats?.partners?.approved || 0,
      subValue: `${stats?.partners?.total || 0} total registered`,
      icon: Store,
      color: "text-green-600",
      bgColor: "bg-green-100"
    },
    {
      title: "Budget Balance",
      value: "TBD",
      subValue: "Funding to be determined",
      icon: DollarSign,
      color: "text-slate-700",
      bgColor: "bg-slate-100"
    }
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Welcome to Soar Your Visibility program management</p>
        </div>
        <div className="flex gap-3">
          <Link to="/beneficiaries">
            <Button variant="outline" className="gap-2" data-testid="add-beneficiary-btn">
              <Plus className="w-4 h-4" />
              Add Beneficiary
            </Button>
          </Link>
          <Link to="/vouchers">
            <Button className="bg-amber-600 hover:bg-amber-700 text-white gap-2" data-testid="issue-voucher-btn">
              <Ticket className="w-4 h-4" />
              Issue Voucher
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={stat.title} className="dashboard-widget stat-card" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                {stat.trend && (
                  <div className={`flex items-center gap-1 text-xs ${stat.trendUp ? 'text-green-600' : 'text-slate-500'}`}>
                    {stat.trendUp ? <TrendingUp className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {stat.trend}
                  </div>
                )}
              </div>
              <div className="mt-4">
                <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                <div className="text-sm text-slate-500 mt-1">{stat.title}</div>
                <div className="text-xs text-slate-400 mt-0.5">{stat.subValue}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Voucher Utilization */}
      <Card className="dashboard-widget">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Voucher Utilization</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Total Issued</span>
              <span className="font-medium">{formatCurrency(stats?.vouchers?.total_value_issued)}</span>
            </div>
            <Progress 
              value={stats?.vouchers?.total_value_issued > 0 
                ? (stats?.vouchers?.total_value_redeemed / stats?.vouchers?.total_value_issued) * 100 
                : 0} 
              className="h-3"
            />
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-amber-500 rounded"></div>
                <span className="text-slate-600">Redeemed: {formatCurrency(stats?.vouchers?.total_value_redeemed)}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-slate-200 rounded"></div>
                <span className="text-slate-600">
                  Remaining: {formatCurrency((stats?.vouchers?.total_value_issued || 0) - (stats?.vouchers?.total_value_redeemed || 0))}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Vouchers */}
        <Card className="dashboard-widget">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Recent Vouchers</CardTitle>
            <Link to="/vouchers">
              <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentVouchers.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Beneficiary</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentVouchers.map((voucher) => (
                    <TableRow key={voucher.id} data-testid={`voucher-row-${voucher.id}`}>
                      <TableCell className="font-mono text-sm">{voucher.code}</TableCell>
                      <TableCell className="text-slate-600">{voucher.beneficiary_name}</TableCell>
                      <TableCell className="font-medium">{formatCurrency(voucher.amount)}</TableCell>
                      <TableCell>{getStatusBadge(voucher.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Ticket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No vouchers issued yet</p>
                <Link to="/vouchers">
                  <Button variant="link" className="text-amber-600 mt-2">Issue your first voucher</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Beneficiaries */}
        <Card className="dashboard-widget">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Recent Beneficiaries</CardTitle>
            <Link to="/beneficiaries">
              <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentBeneficiaries.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentBeneficiaries.map((beneficiary) => (
                    <TableRow key={beneficiary.id} data-testid={`beneficiary-row-${beneficiary.id}`}>
                      <TableCell className="font-medium">
                        {beneficiary.first_name} {beneficiary.last_name}
                      </TableCell>
                      <TableCell className="text-slate-600">{beneficiary.city}, {beneficiary.state}</TableCell>
                      <TableCell>{getStatusBadge(beneficiary.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No beneficiaries registered yet</p>
                <Link to="/beneficiaries">
                  <Button variant="link" className="text-amber-600 mt-2">Register your first beneficiary</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="dashboard-widget bg-gradient-to-r from-slate-900 to-slate-800">
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-white">
              <h3 className="text-xl font-semibold mb-2">Ready to make an impact?</h3>
              <p className="text-slate-300">Start by registering beneficiaries and issuing vouchers to help job seekers.</p>
            </div>
            <div className="flex gap-3">
              <Link to="/partners">
                <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-700" data-testid="add-partner-quick">
                  <Store className="w-4 h-4 mr-2" />
                  Add Partner
                </Button>
              </Link>
              <Link to="/business-plan">
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="view-plan-quick">
                  View Business Plan
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
